/**
 * taxConfigService.js
 *
 * Maps a user's country to their local indirect-tax configuration.
 * Used by TaxSummary to auto-configure labels, rates, and filing periods.
 *
 * Priority for country resolution:
 *   1. localStorage "taxConfigCountry" (manual override set in TaxSummary)
 *   2. user.country (from profile settings)
 *   3. user.timezone (derived via TIMEZONE_COUNTRY map)
 *   4. user.language (last-resort fallback)
 *   5. Default generic config
 */

// ─── Per-country indirect tax configs ────────────────────────────────────────

export const TAX_CONFIGS = {
  GB: {
    taxName: "VAT", taxAbbr: "VAT", reclaimable: true,
    standardRate: 20, rates: [0, 5, 20],
    filingPeriod: "quarterly",
    currencyCode: "GBP", locale: "en-GB",
    thresholdNote: "VAT registration threshold: £90,000 (2024/25).",
    disclaimer: "Figures use invoice issue date (accrual basis). Cash accounting users should use payment dates instead.",
  },
  IE: {
    taxName: "VAT", taxAbbr: "VAT", reclaimable: true,
    standardRate: 23, rates: [0, 9, 13.5, 23],
    filingPeriod: "bimonthly",
    currencyCode: "EUR", locale: "en-IE",
    thresholdNote: "VAT threshold: €80,000 (goods) / €40,000 (services).",
    disclaimer: "Filed via Revenue Online Service (ROS). Verify figures with your accountant.",
  },
  DE: {
    taxName: "Mehrwertsteuer", taxAbbr: "MwSt", reclaimable: true,
    standardRate: 19, rates: [0, 7, 19],
    filingPeriod: "quarterly",
    currencyCode: "EUR", locale: "de-DE",
    thresholdNote: "Kleinunternehmerregelung: €22,000 Jahresumsatz.",
    disclaimer: "Umsatzsteuervoranmeldung beim Finanzamt. Bitte mit Ihrem Steuerberater abstimmen.",
  },
  FR: {
    taxName: "TVA", taxAbbr: "TVA", reclaimable: true,
    standardRate: 20, rates: [0, 2.1, 5.5, 10, 20],
    filingPeriod: "monthly",
    currencyCode: "EUR", locale: "fr-FR",
    thresholdNote: "Franchise en base: €34,400 (services) / €85,800 (ventes).",
    disclaimer: "Déclaration via impots.gouv.fr. Vérifiez avec votre comptable.",
  },
  NL: {
    taxName: "BTW", taxAbbr: "BTW", reclaimable: true,
    standardRate: 21, rates: [0, 9, 21],
    filingPeriod: "quarterly",
    currencyCode: "EUR", locale: "nl-NL",
    thresholdNote: "KOR (Kleineondernemersregeling): €20,000 jaaromzet.",
    disclaimer: "Aangifte via Belastingdienst. Raadpleeg uw accountant.",
  },
  BE: {
    taxName: "TVA/BTW", taxAbbr: "TVA", reclaimable: true,
    standardRate: 21, rates: [0, 6, 12, 21],
    filingPeriod: "quarterly",
    currencyCode: "EUR", locale: "fr-BE",
    thresholdNote: "Franchise TVA: €25,000.",
    disclaimer: "Déclaration via MyMinfin. Vérifiez avec votre comptable.",
  },
  ES: {
    taxName: "IVA", taxAbbr: "IVA", reclaimable: true,
    standardRate: 21, rates: [0, 4, 10, 21],
    filingPeriod: "quarterly",
    currencyCode: "EUR", locale: "es-ES",
    thresholdNote: "No hay umbral de registro en España, todas las empresas deben registrarse.",
    disclaimer: "Declaración trimestral (Modelo 303). Consulte con su asesor fiscal.",
  },
  IT: {
    taxName: "IVA", taxAbbr: "IVA", reclaimable: true,
    standardRate: 22, rates: [0, 4, 5, 10, 22],
    filingPeriod: "quarterly",
    currencyCode: "EUR", locale: "it-IT",
    thresholdNote: "Regime forfettario: €85,000 fatturato annuo.",
    disclaimer: "Dichiarazione tramite Agenzia delle Entrate. Verificare con il commercialista.",
  },
  AU: {
    taxName: "GST", taxAbbr: "GST", reclaimable: true,
    standardRate: 10, rates: [0, 10],
    filingPeriod: "quarterly",
    currencyCode: "AUD", locale: "en-AU",
    thresholdNote: "GST registration threshold: AUD 75,000.",
    disclaimer: "Filed via Business Activity Statement (BAS) on the ATO portal.",
  },
  NZ: {
    taxName: "GST", taxAbbr: "GST", reclaimable: true,
    standardRate: 15, rates: [0, 15],
    filingPeriod: "bimonthly",
    currencyCode: "NZD", locale: "en-NZ",
    thresholdNote: "GST registration threshold: NZD 60,000.",
    disclaimer: "Filed via myIR (Inland Revenue). Verify with your accountant.",
  },
  CA: {
    taxName: "GST/HST", taxAbbr: "GST", reclaimable: true,
    standardRate: 5, rates: [0, 5, 13, 15],
    filingPeriod: "quarterly",
    currencyCode: "CAD", locale: "en-CA",
    thresholdNote: "GST/HST registration threshold: CAD 30,000.",
    disclaimer: "Provincial sales tax (PST/QST) may also apply. HST rates vary by province.",
  },
  SG: {
    taxName: "GST", taxAbbr: "GST", reclaimable: true,
    standardRate: 9, rates: [0, 9],
    filingPeriod: "quarterly",
    currencyCode: "SGD", locale: "en-SG",
    thresholdNote: "GST registration threshold: SGD 1,000,000.",
    disclaimer: "Filed via myTax Portal (IRAS). Verify with your tax advisor.",
  },
  ZA: {
    taxName: "VAT", taxAbbr: "VAT", reclaimable: true,
    standardRate: 15, rates: [0, 15],
    filingPeriod: "bimonthly",
    currencyCode: "ZAR", locale: "en-ZA",
    thresholdNote: "VAT registration threshold: ZAR 1,000,000.",
    disclaimer: "Filed via eFiling (SARS). Verify with your accountant.",
  },
  IN: {
    taxName: "GST", taxAbbr: "GST", reclaimable: true,
    standardRate: 18, rates: [0, 5, 12, 18, 28],
    filingPeriod: "monthly",
    currencyCode: "INR", locale: "en-IN",
    thresholdNote: "GST registration threshold: ₹20 lakh (₹10 lakh for special category states).",
    disclaimer: "Filed via GSTR returns on the GSTN portal. Verify with your CA.",
  },
  US: {
    taxName: "Sales Tax", taxAbbr: "Tax", reclaimable: false,
    standardRate: 0, rates: [0],
    filingPeriod: "quarterly",
    currencyCode: "USD", locale: "en-US",
    thresholdNote: "Economic nexus thresholds vary by state (typically $100,000 or 200 transactions).",
    disclaimer: "US sales tax is collected from customers and remitted to state authorities. No federal input tax recovery applies.",
  },
};

export const DEFAULT_TAX_CONFIG = {
  taxName: "Tax", taxAbbr: "Tax", reclaimable: false,
  standardRate: 0, rates: [0],
  filingPeriod: "quarterly",
  currencyCode: "USD", locale: "en-US",
  thresholdNote: "",
  disclaimer: "Verify all figures with your accountant before submitting any tax return.",
};

// ─── Countries list for profile select ───────────────────────────────────────

export const COUNTRIES = [
  { code: "GB", name: "United Kingdom" },
  { code: "IE", name: "Ireland" },
  { code: "FR", name: "France" },
  { code: "DE", name: "Germany" },
  { code: "NL", name: "Netherlands" },
  { code: "BE", name: "Belgium" },
  { code: "ES", name: "Spain" },
  { code: "IT", name: "Italy" },
  { code: "CH", name: "Switzerland" },
  { code: "SE", name: "Sweden" },
  { code: "PL", name: "Poland" },
  { code: "FI", name: "Finland" },
  { code: "GR", name: "Greece" },
  { code: "RO", name: "Romania" },
  { code: "TR", name: "Turkey" },
  { code: "RU", name: "Russia" },
  { code: "MA", name: "Morocco" },
  { code: "NG", name: "Nigeria" },
  { code: "EG", name: "Egypt" },
  { code: "KE", name: "Kenya" },
  { code: "ZA", name: "South Africa" },
  { code: "US", name: "United States" },
  { code: "CA", name: "Canada" },
  { code: "MX", name: "Mexico" },
  { code: "BR", name: "Brazil" },
  { code: "AR", name: "Argentina" },
  { code: "CO", name: "Colombia" },
  { code: "PE", name: "Peru" },
  { code: "CL", name: "Chile" },
  { code: "AE", name: "UAE" },
  { code: "SA", name: "Saudi Arabia" },
  { code: "PK", name: "Pakistan" },
  { code: "IN", name: "India" },
  { code: "BD", name: "Bangladesh" },
  { code: "TH", name: "Thailand" },
  { code: "SG", name: "Singapore" },
  { code: "HK", name: "Hong Kong" },
  { code: "CN", name: "China" },
  { code: "JP", name: "Japan" },
  { code: "KR", name: "South Korea" },
  { code: "AU", name: "Australia" },
  { code: "NZ", name: "New Zealand" },
];

// ─── Timezone → ISO country code ─────────────────────────────────────────────

const TIMEZONE_COUNTRY = {
  "Europe/London": "GB",
  "Europe/Dublin": "IE",
  "Europe/Lisbon": "PT",
  "Europe/Paris": "FR",
  "Europe/Berlin": "DE",
  "Europe/Amsterdam": "NL",
  "Europe/Brussels": "BE",
  "Europe/Madrid": "ES",
  "Europe/Rome": "IT",
  "Europe/Zurich": "CH",
  "Europe/Stockholm": "SE",
  "Europe/Warsaw": "PL",
  "Europe/Helsinki": "FI",
  "Europe/Athens": "GR",
  "Europe/Bucharest": "RO",
  "Europe/Istanbul": "TR",
  "Europe/Moscow": "RU",
  "Africa/Casablanca": "MA",
  "Africa/Lagos": "NG",
  "Africa/Cairo": "EG",
  "Africa/Nairobi": "KE",
  "Africa/Johannesburg": "ZA",
  "America/New_York": "US",
  "America/Chicago": "US",
  "America/Denver": "US",
  "America/Los_Angeles": "US",
  "America/Phoenix": "US",
  "America/Anchorage": "US",
  "Pacific/Honolulu": "US",
  "America/Toronto": "CA",
  "America/Vancouver": "CA",
  "America/Mexico_City": "MX",
  "America/Bogota": "CO",
  "America/Lima": "PE",
  "America/Sao_Paulo": "BR",
  "America/Buenos_Aires": "AR",
  "America/Santiago": "CL",
  "Asia/Dubai": "AE",
  "Asia/Riyadh": "SA",
  "Asia/Karachi": "PK",
  "Asia/Kolkata": "IN",
  "Asia/Rangoon": "MM",
  "Asia/Dhaka": "BD",
  "Asia/Bangkok": "TH",
  "Asia/Singapore": "SG",
  "Asia/Hong_Kong": "HK",
  "Asia/Shanghai": "CN",
  "Asia/Tokyo": "JP",
  "Asia/Seoul": "KR",
  "Australia/Perth": "AU",
  "Australia/Darwin": "AU",
  "Australia/Brisbane": "AU",
  "Australia/Sydney": "AU",
  "Australia/Melbourne": "AU",
  "Pacific/Auckland": "NZ",
};

// ─── Language → country fallback ─────────────────────────────────────────────

const LANGUAGE_COUNTRY = {
  "en-GB": "GB",
  "en-US": "US",
  "fr": "FR",
  "de": "DE",
  "es": "ES",
};

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Returns the full tax config for a user, plus `countryCode` and `source`.
 * Source is one of: "manual" | "profile" | "timezone" | "language" | "default"
 */
export function getTaxConfig(user, countryOverride) {
  // 1. Manual override passed directly from the caller
  if (countryOverride && TAX_CONFIGS[countryOverride]) {
    return { countryCode: countryOverride, source: "manual", ...TAX_CONFIGS[countryOverride] };
  }

  // 2. Profile country field
  const profileCountry = user?.country;
  if (profileCountry && TAX_CONFIGS[profileCountry]) {
    return { countryCode: profileCountry, source: "profile", ...TAX_CONFIGS[profileCountry] };
  }

  // 3. Timezone
  const tzCountry = TIMEZONE_COUNTRY[user?.timezone];
  if (tzCountry && TAX_CONFIGS[tzCountry]) {
    return { countryCode: tzCountry, source: "timezone", ...TAX_CONFIGS[tzCountry] };
  }

  // 4. Language
  const langCountry = LANGUAGE_COUNTRY[user?.language];
  if (langCountry && TAX_CONFIGS[langCountry]) {
    return { countryCode: langCountry, source: "language", ...TAX_CONFIGS[langCountry] };
  }

  return { countryCode: null, source: "default", ...DEFAULT_TAX_CONFIG };
}
